import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  BarChart3,
  Users,
  Eye,
  Globe,
  Calendar,
  TrendingUp,
  MapPin,
  Shield,
  Download,
  Monitor,
  Smartphone,
  X,
  Trash2,
  CheckCircle,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { hasPageAccess } from '../utils/permissions';

const AnalyticsReport = () => {
  const { user, token, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('today');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [visitorPages, setVisitorPages] = useState([]);
  const [loadingVisitorPages, setLoadingVisitorPages] = useState(false);
  const [visitorTab, setVisitorTab] = useState('desktop'); // 'desktop' or 'mobile'
  const [pagesTab, setPagesTab] = useState('desktop'); // 'desktop' or 'mobile' for top pages
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [showResetResult, setShowResetResult] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Custom scrollbar styles
  const scrollbarStyles = `
    .custom-scrollbar::-webkit-scrollbar {
      width: 4px;
    }
    .custom-scrollbar::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 10px;
    }
    .custom-scrollbar::-webkit-scrollbar-thumb {
      background: #cbd5e1;
      border-radius: 10px;
    }
    .custom-scrollbar::-webkit-scrollbar-thumb:hover {
      background: #94a3b8;
    }
    .custom-scrollbar {
      scrollbar-width: thin;
      scrollbar-color: #cbd5e1 #f1f1f1;
    }
  `;
  const [analytics, setAnalytics] = useState({
    total_views: 0,
    unique_visitors: 0,
    mobile_count: 0,
    desktop_count: 0,
    visitors_list: [],
    top_pages: [],
    top_pages_desktop: [],
    top_pages_mobile: [],
    top_countries: [],
    top_regions: [],
    top_cities: [],
    views_by_date: []
  });

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || (window.location.origin.includes("localhost") ? "http://localhost:8000" : window.location.origin + "/pandvah-backend");

  const getDateRange = () => {
    const now = new Date();
    let start, end;

    switch (dateRange) {
      case 'today':
        start = new Date(now.setHours(0, 0, 0, 0));
        end = new Date();
        break;
      case 'week':
        start = new Date(now.setDate(now.getDate() - 7));
        end = new Date();
        break;
      case 'month':
        start = new Date(now.setDate(now.getDate() - 30));
        end = new Date();
        break;
      case 'custom':
        if (customStartDate && customEndDate) {
          start = new Date(customStartDate);
          end = new Date(customEndDate);
        } else {
          return null;
        }
        break;
      default:
        start = new Date(now.setHours(0, 0, 0, 0));
        end = new Date();
    }

    return {
      start_date: start.toISOString(),
      end_date: end.toISOString()
    };
  };

  const fetchAnalytics = async () => {
    const dateRangeParams = getDateRange();
    if (!dateRangeParams) return;

    setLoading(true);
    try {
      const params = new URLSearchParams(dateRangeParams);
      const response = await fetch(`${API_BASE_URL}/api/analytics/overview?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVisitorClick = async (visitor) => {
    setSelectedVisitor(visitor);
    setLoadingVisitorPages(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/analytics/visitor-pages/${visitor.session_id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setVisitorPages(data.pages);
      }
    } catch (error) {
      console.error('Error fetching visitor pages:', error);
    } finally {
      setLoadingVisitorPages(false);
    }
  };

  const closeVisitorModal = () => {
    setSelectedVisitor(null);
    setVisitorPages([]);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAnalytics();
    setRefreshing(false);
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/analytics/reset`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        // Reset local state
        setAnalytics({
          total_views: 0,
          unique_visitors: 0,
          mobile_count: 0,
          desktop_count: 0,
          visitors_list: [],
          top_pages: [],
          top_pages_desktop: [],
          top_pages_mobile: [],
          top_countries: [],
          top_regions: [],
          top_cities: [],
          views_by_date: []
        });
        setShowResetConfirm(false);
        setResetSuccess(true);
        setShowResetResult(true);
        setTimeout(() => setShowResetResult(false), 2000);
      } else {
        setShowResetConfirm(false);
        setResetSuccess(false);
        setShowResetResult(true);
        setTimeout(() => setShowResetResult(false), 3000);
      }
    } catch (error) {
      console.error('Error resetting analytics:', error);
      setShowResetConfirm(false);
      setResetSuccess(false);
      setShowResetResult(true);
      setTimeout(() => setShowResetResult(false), 3000);
    } finally {
      setResetting(false);
    }
  };

  useEffect(() => {
    if (!authLoading && user && hasPageAccess(user, 'dashboards.web_analytics', ['admin', 'manager', 'owner'])) {
      fetchAnalytics();
    } else if (!authLoading) {
      setLoading(false);
    }
  }, [authLoading, user, dateRange, customStartDate, customEndDate]);

  if (!authLoading && !hasPageAccess(user, 'dashboards.web_analytics', ['admin', 'manager', 'owner'])) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <Shield className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access Analytics Reports.</p>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <>
      <style>{scrollbarStyles}</style>
      <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header with Filter on Right */}
          <div className="bg-white border-b border-gray-200">
            <div className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Web Analytics
                  </h2>
                </div>
                
                {/* Date Range Filter, Refresh and Reset Button */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                  <select
                    value={dateRange}
                    onChange={(e) => setDateRange(e.target.value)}
                    className="text-white text-sm px-3 py-1.5 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition-colors appearance-none pr-8"
                    style={{ 
                      backgroundColor: '#3b82f6',
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
                      backgroundPosition: 'right 6px center',
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: '14px'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
                  >
                    <option value="today" style={{ backgroundColor: 'white', color: 'black' }}>Today</option>
                    <option value="week" style={{ backgroundColor: 'white', color: 'black' }}>Last 7 Days</option>
                    <option value="month" style={{ backgroundColor: 'white', color: 'black' }}>Last 30 Days</option>
                    <option value="custom" style={{ backgroundColor: 'white', color: 'black' }}>Custom Range</option>
                  </select>

                  {dateRange === 'custom' && (
                    <>
                      <input
                        type="date"
                        value={customStartDate}
                        onChange={(e) => setCustomStartDate(e.target.value)}
                        className="text-sm px-3 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <input
                        type="date"
                        value={customEndDate}
                        onChange={(e) => setCustomEndDate(e.target.value)}
                        className="text-sm px-3 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </>
                  )}

                  {/* Refresh Button */}
                  <button
                    onClick={handleRefresh}
                    disabled={refreshing || loading}
                    className="bg-green-500 hover:bg-green-600 text-white text-sm px-3 py-1.5 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <RefreshCw className={`h-3.5 w-3.5 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
                    <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
                  </button>

                  {/* Reset Button - Admin Only */}
                  {user && user.role === 'admin' && (
                    <button
                      onClick={() => setShowResetConfirm(true)}
                      className="bg-red-500 hover:bg-red-600 text-white text-sm px-3 py-1.5 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 transition-colors flex items-center space-x-1"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      <span>Reset</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-blue mx-auto"></div>
                <p className="text-gray-500 mt-4">Loading analytics...</p>
              </div>
            ) : (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                    <div className="flex items-center justify-between mb-1">
                      <Eye className="h-6 w-6 text-theme-blue" />
                    </div>
                    <p className="text-xs text-theme-blue font-medium">Total Page Views</p>
                    <p className="text-2xl font-bold text-blue-900 mt-1">{analytics.total_views.toLocaleString()}</p>
                  </div>

                  <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
                    <div className="flex items-center justify-between mb-1">
                      <Users className="h-6 w-6 text-green-600" />
                    </div>
                    <p className="text-xs text-green-600 font-medium">Unique Visitors</p>
                    <p className="text-2xl font-bold text-green-900 mt-1">{analytics.unique_visitors.toLocaleString()}</p>
                  </div>

                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
                    <div className="flex items-center justify-between mb-1">
                      <Monitor className="h-6 w-6 text-purple-600" />
                    </div>
                    <p className="text-xs text-purple-600 font-medium">Desktop</p>
                    <p className="text-2xl font-bold text-purple-900 mt-1">{analytics.desktop_count.toLocaleString()}</p>
                  </div>

                  <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4 border border-orange-200">
                    <div className="flex items-center justify-between mb-1">
                      <Smartphone className="h-6 w-6 text-orange-600" />
                    </div>
                    <p className="text-xs text-orange-600 font-medium">Mobile</p>
                    <p className="text-2xl font-bold text-orange-900 mt-1">{analytics.mobile_count.toLocaleString()}</p>
                  </div>
                </div>

                {/* Top Pages and Top Cities */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                  {/* Top Pages with Tabs */}
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-base font-semibold text-gray-900 flex items-center">
                        <BarChart3 className="h-4 w-4 mr-2 text-theme-blue" />
                        Top Pages
                      </h3>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setPagesTab('desktop')}
                          className={`px-2 py-1 rounded-md text-xs font-medium transition-colors flex items-center space-x-1 ${
                            pagesTab === 'desktop'
                              ? 'bg-purple-100 text-purple-700'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          <Monitor className="h-3 w-3" />
                          <span>Desktop</span>
                        </button>
                        <button
                          onClick={() => setPagesTab('mobile')}
                          className={`px-2 py-1 rounded-md text-xs font-medium transition-colors flex items-center space-x-1 ${
                            pagesTab === 'mobile'
                              ? 'bg-orange-100 text-orange-700'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          <Smartphone className="h-3 w-3" />
                          <span>Mobile</span>
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2 max-h-60 overflow-y-auto pr-2 custom-scrollbar">
                      {(pagesTab === 'desktop' ? analytics.top_pages_desktop : analytics.top_pages_mobile).length > 0 ? (
                        (pagesTab === 'desktop' ? analytics.top_pages_desktop : analytics.top_pages_mobile).map((page, index) => (
                          <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg mr-1">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {page._id}
                              </p>
                            </div>
                            <span className={`ml-4 px-2 py-1 rounded-full text-xs font-semibold flex-shrink-0 ${
                              pagesTab === 'desktop' 
                                ? 'bg-purple-100 text-purple-700' 
                                : 'bg-orange-100 text-orange-700'
                            }`}>
                              {page.count}
                            </span>
                          </div>
                        ))
                      ) : (
                        <p className="text-gray-500 text-center py-4 text-sm">No {pagesTab} pages data available</p>
                      )}
                    </div>
                  </div>

                  {/* Top Cities */}
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
                      <MapPin className="h-4 w-4 mr-2 text-orange-600" />
                      Top Cities
                    </h3>
                    <div className="space-y-2 max-h-60 overflow-y-auto pr-2 custom-scrollbar">
                      {analytics.top_cities.length > 0 ? (
                        analytics.top_cities.map((city, index) => (
                          <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg mr-1">
                            <div className="flex items-center space-x-2 flex-1 min-w-0">
                              <div className="bg-orange-100 rounded-full p-1.5 flex-shrink-0">
                                <MapPin className="h-3 w-3 text-orange-600" />
                              </div>
                              <span className="text-sm font-medium text-gray-900 truncate">
                                {city._id || 'Unknown'}
                              </span>
                            </div>
                            <span className="ml-4 px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-semibold flex-shrink-0">
                              {city.count}
                            </span>
                          </div>
                        ))
                      ) : (
                        <p className="text-gray-500 text-center py-4 text-sm">No data available</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Unique Visitors List with Tabs */}
                <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-base font-semibold text-gray-900 flex items-center">
                      <Users className="h-4 w-4 mr-2 text-green-600" />
                      Unique Visitors
                    </h3>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setVisitorTab('desktop')}
                        className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center space-x-1 ${
                          visitorTab === 'desktop'
                            ? 'bg-purple-100 text-purple-700'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        <Monitor className="h-3 w-3" />
                        <span>Desktop ({analytics.visitors_list.filter(v => v.device_type === 'desktop').length})</span>
                      </button>
                      <button
                        onClick={() => setVisitorTab('mobile')}
                        className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center space-x-1 ${
                          visitorTab === 'mobile'
                            ? 'bg-orange-100 text-orange-700'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        <Smartphone className="h-3 w-3" />
                        <span>Mobile ({analytics.visitors_list.filter(v => v.device_type === 'mobile').length})</span>
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2 max-h-64 overflow-y-auto pr-2 custom-scrollbar">
                    {analytics.visitors_list.filter(v => v.device_type === visitorTab).length > 0 ? (
                      analytics.visitors_list.filter(v => v.device_type === visitorTab).map((visitor, index) => (
                        <div 
                          key={index} 
                          className="flex items-center justify-between p-2 bg-gray-50 rounded-lg mr-1 cursor-pointer hover:bg-gray-100 transition-colors"
                          onClick={() => handleVisitorClick(visitor)}
                        >
                          <div className="flex items-center space-x-3 flex-1 min-w-0">
                            <div className={`rounded-full p-1.5 flex-shrink-0 ${visitor.device_type === 'mobile' ? 'bg-orange-100' : 'bg-purple-100'}`}>
                              {visitor.device_type === 'mobile' ? (
                                <Smartphone className="h-3 w-3 text-orange-600" />
                              ) : (
                                <Monitor className="h-3 w-3 text-purple-600" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {visitor._id}
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                {visitor.device_name || 'Unknown Device'} • {visitor.os || 'Unknown OS'}
                              </p>
                              <p className="text-xs text-gray-400 truncate">
                                {visitor.city ? `${visitor.city}, ${visitor.country}` : visitor.country || 'Unknown location'}
                              </p>
                            </div>
                          </div>
                          <span className="ml-4 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold flex-shrink-0">
                            {visitor.page_views_count} views
                          </span>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500 text-center py-4 text-sm">No {visitorTab} visitors data available</p>
                    )}
                  </div>
                </div>

                {/* Top Countries and Top States */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                  {/* Top Countries */}
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
                      <Globe className="h-4 w-4 mr-2 text-green-600" />
                      Top Countries
                    </h3>
                    <div className="space-y-2 max-h-60 overflow-y-auto pr-2 custom-scrollbar">
                      {analytics.top_countries.length > 0 ? (
                        analytics.top_countries.map((country, index) => (
                          <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg mr-1">
                            <div className="flex items-center space-x-2 flex-1 min-w-0">
                              <div className="bg-green-100 rounded-full p-1.5 flex-shrink-0">
                                <Globe className="h-3 w-3 text-green-600" />
                              </div>
                              <span className="text-sm font-medium text-gray-900 truncate">
                                {country._id || 'Unknown'}
                              </span>
                            </div>
                            <span className="ml-4 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold flex-shrink-0">
                              {country.count}
                            </span>
                          </div>
                        ))
                      ) : (
                        <p className="text-gray-500 text-center py-4 text-sm">No data available</p>
                      )}
                    </div>
                  </div>

                  {/* Top States/Regions */}
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
                      <MapPin className="h-4 w-4 mr-2 text-purple-600" />
                      Top States
                    </h3>
                    <div className="space-y-2 max-h-60 overflow-y-auto pr-2 custom-scrollbar">
                      {analytics.top_regions.length > 0 ? (
                        analytics.top_regions.map((region, index) => (
                          <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg mr-1">
                            <div className="flex items-center space-x-2 flex-1 min-w-0">
                              <div className="bg-purple-100 rounded-full p-1.5 flex-shrink-0">
                                <MapPin className="h-3 w-3 text-purple-600" />
                              </div>
                              <span className="text-sm font-medium text-gray-900 truncate">
                                {region._id || 'Unknown'}
                              </span>
                            </div>
                            <span className="ml-4 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold flex-shrink-0">
                              {region.count}
                            </span>
                          </div>
                        ))
                      ) : (
                        <p className="text-gray-500 text-center py-4 text-sm">No data available</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Views by Date Chart */}
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
                    <Calendar className="h-4 w-4 mr-2 text-purple-600" />
                    Views Over Time
                  </h3>
                  <div className="space-y-1.5">
                    {analytics.views_by_date.length > 0 ? (
                      analytics.views_by_date.map((day, index) => {
                        const maxViews = Math.max(...analytics.views_by_date.map(d => d.count));
                        const widthPercent = (day.count / maxViews) * 100;
                        
                        return (
                          <div key={index} className="flex items-center space-x-2">
                            <span className="text-xs text-gray-600 w-20 flex-shrink-0">
                              {formatDate(day._id)}
                            </span>
                            <div className="flex-1 bg-gray-100 rounded-full h-6 relative overflow-hidden">
                              <div 
                                className="bg-gradient-to-r from-purple-500 to-purple-600 h-full rounded-full transition-all duration-300 flex items-center justify-end pr-2"
                                style={{ width: `${widthPercent}%` }}
                              >
                                <span className="text-white text-xs font-semibold">
                                  {day.count}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-gray-500 text-center py-4 text-sm">No data available</p>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Visitor Pages Modal */}
        {selectedVisitor && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col">
              {/* Modal Header - Sticky */}
              <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200 sticky top-0 z-10" style={{ backgroundColor: 'var(--theme-primary)' }}>
                <div className="flex items-center space-x-4">
                  <div>
                    <h3 className="text-base font-medium text-white">Visitor Details</h3>
                    <p className="text-xs text-white opacity-90 mt-0.5">
                      IP: {selectedVisitor._id}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      selectedVisitor.device_type === 'mobile' 
                        ? 'bg-orange-100 text-orange-700' 
                        : 'bg-purple-100 text-purple-700'
                    }`}>
                      {selectedVisitor.device_type === 'mobile' ? 'Mobile' : 'Desktop'}
                    </span>
                    <span className="text-xs text-white opacity-90">
                      {selectedVisitor.device_name || 'Unknown Device'}
                    </span>
                  </div>
                  <div className="text-xs text-white opacity-90">
                    {selectedVisitor.os || 'Unknown OS'} • {selectedVisitor.browser || 'Unknown Browser'}
                  </div>
                  <div className="text-xs text-white opacity-90">
                    {selectedVisitor.city ? `${selectedVisitor.city}, ${selectedVisitor.country}` : selectedVisitor.country || 'Unknown location'}
                  </div>
                </div>
              </div>

              {/* Modal Content - Scrollable */}
              <div className="flex-1 overflow-y-auto p-6">
                {loadingVisitorPages ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500 mx-auto"></div>
                    <p className="text-gray-500 mt-4 text-sm">Loading pages...</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {visitorPages.length > 0 ? (
                      visitorPages.map((page, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {page._id}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              {page.timestamps.length} {page.timestamps.length === 1 ? 'visit' : 'visits'}
                            </p>
                          </div>
                          <span className="ml-4 px-3 py-1 bg-cyan-100 text-cyan-700 rounded-full text-sm font-semibold">
                            {page.count}
                          </span>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500 text-center py-8 text-sm">No page data available</p>
                    )}
                  </div>
                )}
              </div>

              {/* Modal Footer - Sticky */}
              <div className="px-6 py-3 border-t border-gray-200 flex justify-end sticky bottom-0 bg-white">
                <button
                  onClick={closeVisitorModal}
                  className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
                  style={{ backgroundColor: 'var(--theme-primary)' }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(33, 150, 183)'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-primary)'}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reset Confirmation Modal */}
        {showResetConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center mb-4">
                <div className="bg-red-100 rounded-full p-3 mr-3">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Reset Analytics Data</h3>
              </div>
              
              <p className="text-gray-600 mb-6">
                Are you sure you want to reset all analytics data? This will permanently delete all page views, visitor data, and statistics. This action cannot be undone.
              </p>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowResetConfirm(false)}
                  disabled={resetting}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReset}
                  disabled={resetting}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 flex items-center justify-center"
                >
                  {resetting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Resetting...
                    </>
                  ) : (
                    'Reset All Data'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reset Result Modal */}
        {showResetResult && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4 text-center shadow-xl">
              {resetSuccess ? (
                <>
                  <CheckCircle className="h-10 w-10 text-green-500 mx-auto mb-3" />
                  <h3 className="text-base font-medium text-gray-900">Reset Successful!</h3>
                  <p className="text-sm text-gray-600 mt-1">All analytics data has been cleared.</p>
                </>
              ) : (
                <>
                  <AlertCircle className="h-10 w-10 text-red-500 mx-auto mb-3" />
                  <h3 className="text-base font-medium text-gray-900">Reset Failed</h3>
                  <p className="text-sm text-gray-600 mt-1">Unable to reset analytics data. Please try again.</p>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default AnalyticsReport;
